import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { CountriesCurrenciesService } from '@app/finxone-web-frontend/app/lib/services/countries-currencies-service/countries-currencies.service';
import { findAndShowAlertFromCode } from '@app/finxone-web-frontend/app/lib/utils/alert-code-utils/alert-code.utils';
import {
  GenericHandleBarModel,
  buildHandleBarModel,
} from '@app/finxone-web-frontend/app/lib/utils/template/model-builder.utils';
import { CardsSuccessCodes } from '@app/translations/default-constants/toaster/success-codes/cards.type';
import { AutoDestroy } from '@finxone-platform/shared/base-types';
import { AlertHandlerService, IconCacheService } from '@finxone-platform/shared/services';
import {
  CardInfoType,
  CardStatusType,
  CardTemplateActionType,
  SecureCardInfoResponse,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { Subject, takeUntil } from 'rxjs';
@Component({
  standalone: false,
  selector: 'finxone-web-frontend-card',
  templateUrl: './card.component.html',
  styleUrl: './card.component.scss',
})
export class CardComponent implements OnInit, OnChanges {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();
  public cardTypeText: string;
  @Input() cardDetails: CardInfoType | undefined;
  @Input() content: string;
  @Input() frozenOverlayContent: string;
  @Input() isFlipped: boolean;
  @Input() secureCVVDetails: SecureCardInfoResponse;
  @Input() backCardTextStyles: {
    label: { 'font-size': string; color: string };
    details: { 'font-size': string; color: string };
  };
  @Input() flippedCardTemplate: string;
  @Output() templateAction = new EventEmitter<string>();
  public templateFunction: HandlebarsTemplateDelegate<any>;
  public renderedContent: string;
  public flippedRenderedContent: string;
  private baseHandleBarModel: GenericHandleBarModel;
  public iconStyle: baseCssConfigStyle;
  readonly CardsSuccessCodes = CardsSuccessCodes;
  // Id selector for every supported template action, e.g. '#activate-card' — see onTemplateClick.
  private readonly templateActionSelector = Object.values(CardTemplateActionType)
    .map((action) => `#${action}`)
    .join(',');

  constructor(
    private store: Store,
    private countriesCurrenciesService: CountriesCurrenciesService,
    private iconCacheService: IconCacheService,
    private clipboard: Clipboard,
    private alertService: AlertHandlerService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    if (this.flippedCardTemplate === undefined) {
      this.flippedCardTemplate = '';
    }
    this.templateFunction = Handlebars.compile(this.content);
    buildHandleBarModel(this.store, '', this.countriesCurrenciesService, this.iconCacheService)
      .pipe(takeUntil(this.destroy$))
      .subscribe((model) => {
        this.baseHandleBarModel = model;
        this.renderedContent = this.createTemplateData(this.cardDetails);
      });
  }

  /**
   * The template a card renders is picked from its status (e.g. the pending template until a
   * physical card is activated), so it can change while the card stays on screen. Recompile on
   * input change, otherwise activating a card leaves the previous template rendered.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // the first render belongs to ngOnInit, once the handlebar model has resolved
    if (!this.baseHandleBarModel || !(changes['content'] || changes['cardDetails'])) {
      return;
    }
    if (changes['content']) {
      this.templateFunction = Handlebars.compile(this.content);
    }
    this.renderedContent = this.createTemplateData(this.cardDetails);
  }

  createTemplateData(cardInfo?: CardInfoType) {
    if (cardInfo) {
      return this.templateFunction({
        ...this.baseHandleBarModel,
        card: cardInfo,
      });
    } else {
      return this.templateFunction({
        ...this.baseHandleBarModel,
      });
    }
  }

  /**
   * The card body is rendered via [innerHTML], so handlebars-authored elements cannot bind Angular
   * listeners. Instead a single click listener sits on the container and we identify actions by the
   * element id, which must match a CardTemplateActionType value.
   *
   * e.g. a template containing
   *   <button id="activate-card">Activate</button>
   * clicked anywhere inside that button emits templateAction = 'activate-card'.
   *
   * closest() is used so a click on a child (icon, span) still resolves to the action element, and
   * clicks elsewhere on the card fall through untouched so the card's own click handling still runs.
   */
  public onTemplateClick(event: MouseEvent): void {
    const actionElement = (event.target as HTMLElement)?.closest(this.templateActionSelector);

    if (!actionElement) {
      return;
    }
    event.stopPropagation();
    this.templateAction.emit(actionElement.id);
  }

  public copyContent(copyContent: string, alertEnum: CardsSuccessCodes) {
    // strip out any spaces or slashes
    const textContent = copyContent.replace(/[\s/]/g, '');
    this.clipboard.copy(textContent);

    findAndShowAlertFromCode(this.store, this.router, this.alertService, [alertEnum]);
  }

  protected readonly CardStatusType = CardStatusType;
}
